import type { CommandHandler } from '../types';
import { flattenRawData } from '../../utils';

/**
 * Handler for 'copyrow' command
 * Copies selected rows via the CopyRow API endpoint
 *
 * Two server response paths:
 * - Persisted (e.g. CrmInvoices): server creates copies in DB, returns NewKeys → refresh grid
 * - Non-persisted: server returns entity field data → add as new unsaved rows inline
 */
export const copyRowHandler: CommandHandler = async (_command, context) => {
  const { selectedRows, copyRows, clearSelection, metadata, onAddRowWithData } = context;

  if (!selectedRows || selectedRows.size === 0) {
    return {
      success: false,
      error: 'Please choose at least one row',
    };
  }

  if (!copyRows) {
    return {
      success: false,
      error: 'Copy operation is not available',
    };
  }

  const nodeKey = metadata.contentKey;
  const rowKeys = Array.from(selectedRows).filter((id) => id && !id.startsWith('temp_'));

  if (rowKeys.length === 0) {
    return {
      success: false,
      error: 'No saved rows selected to copy',
    };
  }

  try {
    // Response shape: { data: { Code, Persisted?, Data?, NewKeys?, KeyField? } }
    const response = await copyRows({ nodeKey, rowKeys });
    const payload = (response.data || response.Data || response) as Record<string, unknown>;

    // Persisted path: server already created copies in DB
    if (payload.Persisted || payload.persisted) {
      clearSelection?.();
      return {
        success: true,
        shouldRefresh: true,
        message: 'Rows copied successfully',
      };
    }

    // Non-persisted path: server returns field data to add inline
    const loadResult = payload.Data || payload.data;
    if (loadResult && onAddRowWithData) {
      const keyField = (payload.KeyField || payload.keyField) as string | undefined;

      // loadResult may be a LoadResult ({ data: [...], totalCount: N }) or a raw array.
      // Guard against primitives / unexpected shapes from the API.
      let rawArray: unknown[];
      if (Array.isArray(loadResult)) {
        rawArray = loadResult;
      } else if (typeof loadResult === 'object' && loadResult !== null) {
        const inner =
          (loadResult as Record<string, unknown>).data ??
          (loadResult as Record<string, unknown>).Data;
        rawArray = Array.isArray(inner) ? inner : [];
      } else {
        rawArray = [];
      }

      const rows = flattenRawData(rawArray);

      // Strip the key field so new rows don't carry the original's key
      if (keyField) {
        rows.forEach((row) => {
          delete row[keyField];
        });
      }

      onAddRowWithData(rows);

      clearSelection?.();
      return {
        success: true,
        shouldRefresh: false,
        message: `${rows.length} row(s) copied — save to persist`,
      };
    }

    // Fallback: just refresh
    clearSelection?.();
    return {
      success: true,
      shouldRefresh: true,
      message: 'Rows copied successfully',
    };
  } catch (error: unknown) {
    clearSelection?.();
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to copy rows',
    };
  }
};
